import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from 'react'
import { useDisplayReport } from '../home/displayReport'
import { canPrefetch } from '../lib/mapRoute'
import { selectedPlace, useApp } from '../store'
import { installSheetOrigin } from '../v4/overlays/sheetOrigin'
import { useUi, type Sheet as SheetState } from '../ui'
import { atIdle } from '../../v5/zones/radar/idle'
import { focusAfterLayout } from '../lib/focusTrap'

type SheetModules = typeof import('./sheets')

// The sheets are their own chunk, held here once loaded instead of behind React.lazy: a lazy
// component that suspends on first open makes React hold the reveal back ~300 ms (its
// Suspense throttle), which was most of the first sheet's cost. With the module in hand (the
// chunk is warmed early, below) the sheet renders in the same commit as the tap.
let modules: SheetModules | null = null
let loading: Promise<void> | null = null
const subscribers = new Set<() => void>()

function load(): Promise<void> {
  loading ??= import('./sheets').then(
    (m) => {
      modules = m
      for (const f of subscribers) f()
    },
    () => {
      loading = null // offline / evicted chunk: the next open tries again
    },
  )
  return loading
}

function useSheetModules(): SheetModules | null {
  return useSyncExternalStore(
    (cb) => {
      subscribers.add(cb)
      return () => subscribers.delete(cb)
    },
    () => modules,
    () => null,
  )
}

type IdleWindow = Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number; cancelIdleCallback?: (id: number) => void }

/**
 * Warm the sheets chunk so the first morph isn't delayed: when the browser is idle (with a
 * deadline, so a busy first seconds can't starve it) and the moment a card is hovered, focused
 * or touched — whichever comes first. Once it is in, the Time Machine ("Past weather") chunk, which
 * the sheets chunk loads lazily on its own, is fetched at the next idle moment too (unless the
 * connection is slow or data-saving), so its first open is a render, not a download; `afterTimeMachine`
 * then runs (the app uses it to fetch the range Past weather opens on). Returns the cleanup.
 */
export function preloadSheets(opts: { afterTimeMachine?: () => void } = {}): () => void {
  const w = window as IdleWindow
  let done = false
  let stopped = false
  let second = 0
  const warm = () => {
    if (done) return
    done = true
    off()
    void load().then(() => {
      if (stopped || !modules || !canPrefetch()) return
      const go = () => void Promise.resolve(modules?.warmTimeMachine()).then(() => opts.afterTimeMachine?.(), () => undefined)
      second = w.requestIdleCallback ? w.requestIdleCallback(go, { timeout: 2500 }) : window.setTimeout(go, 1200)
    })
  }
  const near = (e: Event) => {
    if ((e.target as Element | null)?.closest?.('[data-card], .ch-toolbar, .ch-bottom, [data-m], [data-grow], .ladder-row, .instr, .hero button')) warm()
  }
  const events = ['pointerover', 'focusin', 'touchstart'] as const
  for (const ev of events) document.addEventListener(ev, near, { passive: true, capture: true })
  const idle = w.requestIdleCallback ? w.requestIdleCallback(warm, { timeout: 900 }) : window.setTimeout(warm, 900)
  function off() {
    for (const ev of events) document.removeEventListener(ev, near, { capture: true })
    if (w.cancelIdleCallback) w.cancelIdleCallback(idle)
    else window.clearTimeout(idle)
  }
  return () => {
    done = stopped = true
    off()
    if (second) {
      if (w.cancelIdleCallback) w.cancelIdleCallback(second)
      else window.clearTimeout(second)
    }
  }
}

const TITLES: Record<Exclude<SheetState['kind'], 'detail' | 'settings'>, string> = {
  sources: 'Sources & methods',
  alert: 'Weather alert',
  timemachine: 'Time Machine',
}

export function SheetHost({ retained = false }: { retained?: boolean }) {
  // Remember which control opened a sheet, so the sheet can grow out of it (v4/overlays/sheetOrigin.ts).
  useEffect(() => installSheetOrigin(), [])
  const sheet = useUi((s) => s.sheet)
  const close = useUi((s) => s.close)
  const place = useApp(selectedPlace)
  const q = useDisplayReport(place)
  const report = q.data
  const wasOpen = useRef(false)
  const localSky = useRef<string | null>(null)
  const localKey = sheet?.kind === 'detail' && sheet.day === 0 && (sheet.metric === 'moon' || sheet.metric === 'sun') ? sheet.metric : null
  if (!sheet) localSky.current = null
  if (!report && localKey) localSky.current = localKey

  const onClose = useCallback(() => close(), [close])

  // Restore focus to whatever opened the sheet (also after Back closed it).
  useEffect(() => {
    if (sheet) {
      wasOpen.current = true
      return
    }
    if (!wasOpen.current) return
    wasOpen.current = false
    const el = useUi.getState().returnFocus
    if (el && document.contains(el)) return focusAfterLayout(el)
  }, [sheet])

  const M = useSheetModules()
  const [preparedReport, setPreparedReport] = useState<typeof report>()
  useEffect(() => {
    if (!retained || !M || !report || sheet) return
    return atIdle(() => setPreparedReport(report))
  }, [retained, M, report, sheet])
  useEffect(() => {
    if (sheet && !M) void load()
  }, [sheet, M])

  let node: ReactNode = null
  if (sheet && M) {
    const { DetailSheet, SourcesSheet, SettingsSheet, AlertSheet, TimeMachineSheet, PendingSheet, LocalSkySheet } = M
    // Only morph from a real card; toolbar-opened sheets rise and fade instead.
    const layoutId = sheet.origin || undefined
    if (sheet.kind === 'settings') node = <SettingsSheet key="settings" layoutId={layoutId} onClose={onClose} />
    else if (sheet.kind === 'detail' && sheet.day === 0 && (!report || (localKey && localSky.current === localKey)) && (sheet.metric === 'moon' || sheet.metric === 'sun' || sheet.metric === 'uv'))
      node = <LocalSkySheet key="local-sky" place={place} metric={sheet.metric} onClose={onClose} />
    else if (!report)
      // Opened from a link before the forecast is in (or it failed): the sheet says so.
      node = (
        <PendingSheet
          key="pending"
          title={sheet.kind === 'detail' ? 'Forecast details' : TITLES[sheet.kind]}
          place={place.isCurrent ? 'My Location' : place.label}
          error={q.isError ? q.error : null}
          onRetry={() => void q.refetch()}
          onClose={onClose}
        />
      )
    else if (sheet.kind === 'detail')
      node = <DetailSheet key="detail" report={report} metric={sheet.metric} day={sheet.day} layoutId={layoutId} onClose={onClose} />
    else if (sheet.kind === 'sources')
      node = <SourcesSheet key="sources" report={report} place={place} layoutId={layoutId} onClose={onClose} />
    else if (sheet.kind === 'alert')
      node = <AlertSheet key="alert" report={report} alertId={sheet.alertId} layoutId={layoutId} onClose={onClose} />
    else if (sheet.kind === 'timemachine')
      node = <TimeMachineSheet key="timemachine" report={report} place={place} tab={sheet.tab} layoutId={layoutId} onClose={onClose} />
  }
  const prepared = useMemo(() => retained && preparedReport && M ? <M.DetailSheet key="detail" report={preparedReport} metric="conditions" day={0} onClose={onClose} /> : undefined, [retained, preparedReport, M, onClose])
  return M ? <M.SheetPresence prepared={prepared}>{node}</M.SheetPresence> : null
}
